import { useState, useEffect } from 'react';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ImageIcon from '@mui/icons-material/Image';
import RichTextEditor from '../Layouts/RichTextEditor';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const blockTypeLabels = {
    image: "Image",
    code: "Code Block",
    text: "Text",
    links: "Links",
};

const emptyBlock = (type) => {
    if (type === "image") return { type, image: { url: "" }, link: "" };
    if (type === "code") return { type, code: "" };
    if (type === "text") return { type, content: "", contentZh: "" };
    return { type: "links", links: [{ name: "", nameZh: "", url: "" }] };
};

const FooterColumnForm = () => {
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(isEditMode);
    const [saving, setSaving] = useState(false);

    const [side, setSide] = useState("left");
    const [heading, setHeading] = useState("");
    const [headingZh, setHeadingZh] = useState("");
    const [hideHeading, setHideHeading] = useState(false);
    const [headingSize, setHeadingSize] = useState("sm");
    const [order, setOrder] = useState(0);
    const [active, setActive] = useState(true);
    const [blocks, setBlocks] = useState([emptyBlock("text")]);

    useEffect(() => {
        if (!isEditMode) return;

        const fetchColumn = async () => {
            try {
                const { data } = await axios.get('/api/v1/admin/footer-columns');
                const column = data.columns.find((c) => c._id === id);
                if (!column) {
                    enqueueSnackbar("Footer column not found", { variant: "error" });
                    navigate('/admin/footer-columns');
                    return;
                }
                setSide(column.side);
                setHeading(column.heading);
                setHeadingZh(column.headingZh || "");
                setHideHeading(Boolean(column.hideHeading));
                setHeadingSize(column.headingSize || "sm");
                setOrder(column.order || 0);
                setActive(column.active);
                setBlocks(column.blocks && column.blocks.length > 0 ? column.blocks : [emptyBlock("text")]);
            } catch (error) {
                enqueueSnackbar(error?.response?.data?.message || "Failed to load column", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchColumn();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const updateBlock = (index, patch) => {
        setBlocks((prev) => prev.map((b, i) => i === index ? { ...b, ...patch } : b));
    };

    const addBlock = (type) => {
        setBlocks((prev) => [...prev, emptyBlock(type)]);
    };

    const removeBlock = (index) => {
        setBlocks((prev) => prev.filter((_, i) => i !== index));
    };

    const moveBlock = (index, direction) => {
        setBlocks((prev) => {
            const next = [...prev];
            const target = index + direction;
            if (target < 0 || target >= next.length) return prev;
            [next[index], next[target]] = [next[target], next[index]];
            return next;
        });
    };

    const handleBlockImageChange = (index, e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            if (reader.readyState === 2) {
                updateBlock(index, { image: { url: reader.result } });
            }
        };
        reader.readAsDataURL(file);
    };

    const updateBlockLink = (blockIndex, linkIndex, field, value) => {
        setBlocks((prev) => prev.map((b, i) => {
            if (i !== blockIndex) return b;
            const links = b.links.map((l, j) => j === linkIndex ? { ...l, [field]: value } : l);
            return { ...b, links };
        }));
    };

    const addBlockLink = (blockIndex) => {
        setBlocks((prev) => prev.map((b, i) => i === blockIndex ? { ...b, links: [...b.links, { name: "", nameZh: "", url: "" }] } : b));
    };

    const removeBlockLink = (blockIndex, linkIndex) => {
        setBlocks((prev) => prev.map((b, i) => i === blockIndex ? { ...b, links: b.links.filter((_, j) => j !== linkIndex) } : b));
    };

    const submitHandler = async (e) => {
        e.preventDefault();

        if (!heading.trim()) {
            enqueueSnackbar("Please enter a heading", { variant: "warning" });
            return;
        }

                const payload = { side, heading, headingZh, hideHeading, order, active, blocks };

        setSaving(true);
        try {
            if (isEditMode) {
                await axios.put(`/api/v1/admin/footer-column/${id}`, payload);
                enqueueSnackbar("Footer column updated", { variant: "success" });
            } else {
                await axios.post('/api/v1/admin/footer-column/new', payload);
                enqueueSnackbar("Footer column created", { variant: "success" });
            }
            navigate('/admin/footer-columns');
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to save column", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title={`Admin: ${isEditMode ? "Edit" : "New"} Footer Column | 1313 Mart`} />
            {saving && <BackdropLoader />}

            <form onSubmit={submitHandler} className="flex flex-col gap-4 bg-white rounded-lg shadow p-4 sm:p-6" id="footercolumnform">

                <h1 className="text-lg font-medium uppercase">{isEditMode ? "Edit" : "New"} Footer Column</h1>

                <div className="flex flex-col sm:flex-row gap-4">
                    <TextField label="Heading" variant="outlined" size="small" required fullWidth value={heading} onChange={(e) => setHeading(e.target.value)} />
                    <TextField label="Heading (中文 — optional)" variant="outlined" size="small" fullWidth value={headingZh} onChange={(e) => setHeadingZh(e.target.value)} />
                    <TextField label="Heading Size" select variant="outlined" size="small" fullWidth value={headingSize} onChange={(e) => setHeadingSize(e.target.value)}>
                        <MenuItem value="sm">Small</MenuItem>
                        <MenuItem value="md">Medium</MenuItem>
                        <MenuItem value="lg">Large</MenuItem>
                    </TextField>
                    <TextField label="Side" select variant="outlined" size="small" fullWidth value={side} onChange={(e) => setSide(e.target.value)}>
                        <MenuItem value="left">Left</MenuItem>
                        <MenuItem value="right">Right</MenuItem>
                    </TextField>
                    <TextField label="Display Order" type="number" variant="outlined" size="small" value={order} onChange={(e) => setOrder(e.target.value)} sx={{ maxWidth: 160 }} />
                </div>

                <FormControlLabel control={<Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} />} label="Active (visible in footer)" />
                <FormControlLabel control={<Checkbox checked={hideHeading} onChange={(e) => setHideHeading(e.target.checked)} />} label="Hide heading text on the live footer" />

                <div className="flex flex-col gap-4">
                    <p className="text-sm text-gray-600">Content Blocks (shown top to bottom in this order)</p>

                    {blocks.map((block, i) => (
                        <div key={i} className="border rounded-lg p-4 flex flex-col gap-3 bg-gray-50">
                            <div className="flex justify-between items-center">
                                <span className="text-xs font-semibold uppercase text-gray-500">{blockTypeLabels[block.type]}</span>
                                <div className="flex items-center gap-1">
                                    <button type="button" onClick={() => moveBlock(i, -1)} className="text-gray-500 hover:text-gray-700 p-1"><ArrowUpwardIcon sx={{ fontSize: "18px" }} /></button>
                                    <button type="button" onClick={() => moveBlock(i, 1)} className="text-gray-500 hover:text-gray-700 p-1"><ArrowDownwardIcon sx={{ fontSize: "18px" }} /></button>
                                    <button type="button" onClick={() => removeBlock(i)} className="text-red-500 hover:text-red-700 p-1"><DeleteIcon sx={{ fontSize: "18px" }} /></button>
                                </div>
                            </div>

                            {block.type === "image" && (
                                <div className="flex flex-col gap-3">
                                    <div className="flex items-center gap-4">
                                        <div className="w-32 h-20 flex items-center justify-center border rounded-lg overflow-hidden bg-white">
                                            {!block.image?.url ? <ImageIcon className="text-gray-400" /> :
                                                <img draggable="false" src={block.image.url} alt="Block" className="w-full h-full object-cover" />
                                            }
                                        </div>
                                        <label className="rounded bg-gray-400 text-center cursor-pointer text-white py-2 px-3 shadow hover:shadow-lg text-sm">
                                            <input type="file" accept="image/*" onChange={(e) => handleBlockImageChange(i, e)} className="hidden" />
                                            Choose Image
                                        </label>
                                    </div>
                                    <TextField label="Click-through Link (optional)" variant="outlined" size="small" fullWidth value={block.link || ""} onChange={(e) => updateBlock(i, { link: e.target.value })} />
                                    <TextField
                                        label="Width (%)"
                                        type="number"
                                        variant="outlined"
                                        size="small"
                                        value={block.width ?? 100}
                                        onChange={(e) => updateBlock(i, { width: Number(e.target.value) })}
                                        inputProps={{ min: 5, max: 100 }}
                                        sx={{ maxWidth: 160 }}
                                        helperText="% of the column's width"
                                    />
                                </div>
                            )}

                            {block.type === "code" && (
                                <TextField
                                    label="HTML / Embed Code"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    multiline
                                    minRows={4}
                                    value={block.code || ""}
                                    onChange={(e) => updateBlock(i, { code: e.target.value })}
                                    helperText="Paste raw HTML here — e.g. a newsletter signup form's embed code"
                                />
                            )}

                            {block.type === "text" && (
                                <div className="flex flex-col gap-2">
                                    <RichTextEditor value={block.content || ""} onChange={(val) => updateBlock(i, { content: val })} />
                                    <p className="text-xs text-gray-500 mt-1">中文 — optional</p>
                                    <RichTextEditor value={block.contentZh || ""} onChange={(val) => updateBlock(i, { contentZh: val })} />
                                </div>
                            )}

                            {block.type === "links" && (
                                <div className="flex flex-col gap-2">
                                    {block.links.map((link, j) => (
                                        <div key={j} className="flex items-center gap-2">
                                            <TextField label="Link Text" variant="outlined" size="small" value={link.name} onChange={(e) => updateBlockLink(i, j, "name", e.target.value)} />
                                            <TextField label="Link Text (中文)" variant="outlined" size="small" value={link.nameZh || ""} onChange={(e) => updateBlockLink(i, j, "nameZh", e.target.value)} />
                                            <TextField label="URL" variant="outlined" size="small" fullWidth value={link.url} onChange={(e) => updateBlockLink(i, j, "url", e.target.value)} />
                                            <button type="button" onClick={() => removeBlockLink(i, j)} className="text-red-500 hover:text-red-700 p-2"><DeleteIcon sx={{ fontSize: "18px" }} /></button>
                                        </div>
                                    ))}
                                    <button type="button" onClick={() => addBlockLink(i)} className="flex items-center gap-1 text-sm text-primary-blue hover:underline w-fit">
                                        <AddIcon sx={{ fontSize: "16px" }} /> Add Link
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}

                    <div className="flex flex-wrap gap-2">
                        {Object.keys(blockTypeLabels).map((type) => (
                            <button key={type} type="button" onClick={() => addBlock(type)} className="flex items-center gap-1 text-sm border border-primary-blue text-primary-blue px-3 py-1.5 rounded hover:bg-blue-50">
                                <AddIcon sx={{ fontSize: "16px" }} /> {blockTypeLabels[type]}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex justify-end">
                    <input form="footercolumnform" type="submit" className="bg-primary-orange uppercase px-6 py-3 text-white font-medium rounded shadow hover:shadow-lg cursor-pointer" value={isEditMode ? "Save Changes" : "Create Column"} />
                </div>

            </form>
        </>
    );
};

export default FooterColumnForm;
